import React, { useState, useEffect } from "react";
import {
  ArrowLeft,
  Save,
  Plus,
  UserPlus,
  Shield,
  X,
  Check,
  Tag,
  Database,
  MapPin,
  Send,
} from "lucide-react";
import {
  facilitiesApi,
  facilityServicesAuthApi,
  usersApi,
  verticalsApi,
  authFetch,
} from "../../utils/api";
import {
  DEFAULT_INVITE_DESTINATION,
  INVITE_DESTINATIONS,
  PRODUCT_CODES,
  SERVICE_TYPES,
} from "../../constants";
import { BuildIQConfig } from "../construction/BuildIQConfig";
const SERVICE_STATUSES = [
  "provisioning",
  "active",
  "suspended",
  "decommissioned",
];
const SERVICE_ACCESS_ROLES = [
  "none",
  "viewer",
  "user",
  "editor",
  "approver",
  "admin",
];

const FACILITY_ROLES_BY_PRODUCT = [
  {
    product: "rag",
    label: "Knowledge Base",
    Icon: Database,
    roles: [
      {
        value: "kb_steward",
        label: "KB Steward",
        description: "Can manage knowledge base documents and review queue",
        color: "bg-emerald-100 text-emerald-800",
      },
      {
        value: "broadcasts_composer",
        label: "Broadcasts Composer",
        description: "Can write and send broadcasts to facility members",
        color: "bg-orange-100 text-orange-800",
      },
    ],
  },
  {
    product: "fieldiq",
    label: "FieldIQ",
    Icon: MapPin,
    roles: [
      {
        value: "ld_leadership",
        label: "LD Leadership",
        description: "District leadership designation — elevated field access",
        color: "bg-purple-100 text-purple-800",
      },
      {
        value: "field_supervisor",
        label: "Field Supervisor",
        description: "Can manage field routes and canvasser activity",
        color: "bg-blue-100 text-blue-800",
      },
    ],
  },
  {
    product: "telegram_bot",
    label: "Telegram Bot",
    Icon: Send,
    roles: [
      {
        value: "telegram_moderator",
        label: "Telegram Moderator",
        description: "Can review and action Telegram Bot moderation violations",
        color: "bg-cyan-100 text-cyan-800",
      },
    ],
  },
];

const SERVICE_TO_ROLE_GROUPS = {
  rag: ["rag", "fieldiq"],
  slm: [],
  private_llm: [],
  telegram_bot: ["telegram_bot"],
  voip: [],
};

const FACILITY_ROLES_FLAT = FACILITY_ROLES_BY_PRODUCT.flatMap((p) => p.roles);
const ROLE_COLOR_MAP = Object.fromEntries(
  FACILITY_ROLES_FLAT.map((r) => [r.value, r.color]),
);
const ROLE_LABEL_MAP = Object.fromEntries(
  FACILITY_ROLES_FLAT.map((r) => [r.value, r.label]),
);

const FacilityRolesBadges = ({ roles }) => {
  if (!roles || roles.length === 0)
    return <span className="text-xs text-slate-400">None</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {roles.map((r) => (
        <span
          key={r}
          className={`px-2 py-0.5 text-xs font-medium rounded-full ${ROLE_COLOR_MAP[r] || "bg-slate-100 text-slate-700"}`}
          title={ROLE_LABEL_MAP[r] || r}
        >
          {ROLE_LABEL_MAP[r] || r}
        </span>
      ))}
    </div>
  );
};

const VERTICALS = [
  { value: "healthcare", label: "Healthcare" },
  { value: "legal", label: "Legal" },
  { value: "finance", label: "Finance" },
  { value: "manufacturing", label: "Manufacturing" },
  { value: "retail", label: "Retail" },
  { value: "construction", label: "Construction" },
  { value: "political", label: "Political" },
  { value: "informational", label: "Informational" },
];

const MODEL_TIERS = [
  { value: "basic", label: "Basic (CPU)" },
  { value: "medium", label: "Medium (T4 Small)" },
  { value: "elite", label: "Elite (T4 Medium)" },
  { value: "premium", label: "Premium (A10G)" },
];

const BASE_MODELS = [
  { value: "biomistral-7b", label: "BioMistral-7B (Medical)" },
  { value: "mistral-7b", label: "Mistral-7B-Instruct (General)" },
  { value: "gemma-7b-it", label: "Gemma-7B-IT (General)" },
];

const TIMEZONES = [
  { value: "America/New_York", label: "Eastern" },
  { value: "America/Chicago", label: "Central" },
  { value: "America/Denver", label: "Mountain" },
  { value: "America/Phoenix", label: "Arizona" },
  { value: "America/Los_Angeles", label: "Pacific" },
  { value: "UTC", label: "UTC" },
];

const OpenAIKeyCard = ({ facilityId, hint: initialHint }) => {
  const [hint, setHint] = useState(initialHint || null);
  const [input, setInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    setError("");
    setSaved(false);
    const res = await authFetch(`/admin/facilities/${facilityId}/openai-key`, {
      method: "PATCH",
      body: JSON.stringify({ openai_api_key: input }),
    });
    setSaving(false);
    if (res?.error) {
      setError(res.error);
    } else {
      setHint(res.openai_api_key_hint);
      setInput("");
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    }
  };

  const handleClear = async () => {
    if (!window.confirm("Remove the OpenAI API key for this facility?")) return;
    setSaving(true);
    setError("");
    const res = await authFetch(`/admin/facilities/${facilityId}/openai-key`, {
      method: "PATCH",
      body: JSON.stringify({ openai_api_key: "" }),
    });
    setSaving(false);
    if (res?.error) setError(res.error);
    else {
      setHint(null);
      setInput("");
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
      <h4 className="text-base font-semibold text-slate-800 mb-4">
        OpenAI API Key
      </h4>
      <div className="space-y-3">
        {hint && (
          <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
            <span className="text-sm font-mono text-slate-600">{hint}</span>
            <button
              onClick={handleClear}
              disabled={saving}
              className="text-xs text-red-500 hover:text-red-700 disabled:opacity-50"
            >
              Remove
            </button>
          </div>
        )}
        <div className="flex gap-2">
          <input
            type="password"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={hint ? "Enter new key to replace…" : "sk-…"}
            className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-blue-400 focus:outline-none font-mono"
          />
          <button
            onClick={handleSave}
            disabled={!input.trim() || saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5"
          >
            <Save className="w-4 h-4" />
            {saving ? "Saving…" : saved ? "Saved!" : "Save"}
          </button>
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <p className="text-xs text-slate-400">
          Used for KB vector search and AI enrichment. Falls back to the
          server-wide key if not set.
        </p>
      </div>
    </div>
  );
};

const FacilityDetail = ({ facility, onBack, onSaved }) => {
  // Where invited users land. Not window.location.origin: that sent an
  // invited user to whichever console the admin was in.
  const [inviteDestination, setInviteDestination] = useState(
    DEFAULT_INVITE_DESTINATION,
  );
  const facilityId = facility.facility_id || facility.facilityId;
  const facilityName =
    facility.facility_name || facility.facilityName || facilityId;

  // ── DETAILS ──────────────────────────────────────────────────────────
  const [formData, setFormData] = useState({
    facility_id: facility.facility_id || facility.facilityId || "",
    facility_name: facility.facility_name || facility.facilityName || "",
    facility_type:
      facility.facility_type || facility.facilityType || "business",
    contact_email: facility.contact_email || facility.contactEmail || "",
    contact_phone: facility.contact_phone || facility.contactPhone || "",
    address: facility.address || "",
    timezone: facility.timezone || "America/New_York",
    status: facility.status || "active",
    vertical_id: facility.vertical_id || facility.verticalId || "retail",
    base_model_id:
      facility.base_model_id || facility.baseModelId || "mistral-7b",
    model_tier: facility.model_tier || facility.modelTier || "premium",
    training_enabled:
      facility.training_enabled ?? facility.trainingEnabled ?? true,
    webhook_url: facility.webhook_url || facility.webhookUrl || "",
    webhook_enabled:
      facility.webhook_enabled ?? facility.webhookEnabled ?? false,
    display_name: facility.display_name || facility.displayName || "",
    state: facility.state || "",
    agent_name: facility.agent_name || facility.agentName || "",
    brand_color: facility.brand_color || facility.brandColor || "#cc1111",
    static_base_url: facility.static_base_url || facility.staticBaseUrl || "",
    allowed_origins: facility.allowed_origins || facility.allowedOrigins || "",
    system_prompt: facility.system_prompt || facility.systemPrompt || "",
    enrichment_rules:
      typeof facility.enrichment_rules === "object"
        ? JSON.stringify(facility.enrichment_rules, null, 2)
        : facility.enrichment_rules || facility.enrichmentRules || "[]",
    sheets_log_tab: facility.sheets_log_tab || facility.sheetsLogTab || "Logs",
    source_registry_namespace:
      facility.source_registry_namespace ||
      facility.sourceRegistryNamespace ||
      "",
    // FieldIQ tab override: null = use vertical defaults, array = explicit set
    enabled_tabs: facility.enabled_tabs ?? null,
  });
  const [savingDetails, setSavingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState(null);
  const [detailsSaved, setDetailsSaved] = useState(false);

  // ── FIELDIQ TABS ─────────────────────────────────────────────────────
  // Tab definitions come from the facility's vertical (verticals.tabs);
  // facilities.enabled_tabs narrows them per facility.
  const [verticalTabsMap, setVerticalTabsMap] = useState({});

  useEffect(() => {
    verticalsApi.getAll().then((rows) => {
      const map = {};
      rows.forEach((v) => {
        map[v.vertical_id] = Array.isArray(v.tabs) ? v.tabs : [];
      });
      setVerticalTabsMap(map);
    });
  }, []);

  const fieldiqEntries = [
    ...(verticalTabsMap[formData.vertical_id] || []),
  ].sort((a, b) => (a.order || 0) - (b.order || 0));
  // verticals.tabs holds two kinds of entry. No `kind` means a nav tab;
  // `kind: "feature"` is a switch for something inside a tab (FieldIQ keeps
  // those out of the tab bar). They share one array so they share this editor
  // and facilities.enabled_tabs — but listing a feature under a heading that
  // says "Tabs" would misdescribe what the checkbox does.
  const fieldiqTabs = fieldiqEntries.filter((t) => t.kind !== "feature");
  const fieldiqFeatures = fieldiqEntries.filter((t) => t.kind === "feature");
  // Built from every entry, tabs AND features. enabled_tabs is one flat array,
  // so the moment the admin ticks anything the whole set is written — leaving
  // features out here would silently switch them off on the first click.
  const defaultTabIds = fieldiqEntries
    .filter((t) => t.default_enabled !== false)
    .map((t) => t.id);
  const effectiveTabIds =
    formData.enabled_tabs == null ? defaultTabIds : formData.enabled_tabs;

  const toggleTab = (tabId) => {
    const next = effectiveTabIds.includes(tabId)
      ? effectiveTabIds.filter((id) => id !== tabId)
      : [...effectiveTabIds, tabId];
    setFormData({ ...formData, enabled_tabs: next });
  };

  const handleSaveDetails = async () => {
    setSavingDetails(true);
    setDetailsError(null);
    const result = await facilitiesApi.update(facility.id, formData);
    setSavingDetails(false);
    if (result && result.error) {
      setDetailsError(result.error);
    } else {
      setDetailsSaved(true);
      setTimeout(() => setDetailsSaved(false), 2000);
      if (onSaved) onSaved();
    }
  };

  // ── SERVICES ─────────────────────────────────────────────────────────
  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceEdits, setServiceEdits] = useState({});
  const [savingService, setSavingService] = useState(null);
  const [serviceError, setServiceError] = useState(null);
  const [showAddService, setShowAddService] = useState(false);
  const [newService, setNewService] = useState({
    service_type: "rag",
    status: "provisioning",
    config: "{}",
  });

  const fetchServices = async () => {
    setLoadingServices(true);
    const data = await facilityServicesAuthApi.getByFacility(facilityId);
    setServices(data);
    setLoadingServices(false);
  };

  useEffect(() => {
    fetchServices();
  }, [facilityId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEditService = (svc) => {
    setEditingService(svc.id);
    setServiceEdits({
      status: svc.status,
      config:
        typeof svc.config === "object"
          ? JSON.stringify(svc.config, null, 2)
          : svc.config || "{}",
    });
  };

  const handleSaveService = async (svc) => {
    setSavingService(svc.id);
    setServiceError(null);
    let config;
    try {
      config = JSON.parse(serviceEdits.config || "{}");
    } catch {
      setServiceError("Invalid JSON in config");
      setSavingService(null);
      return;
    }
    const result = await facilityServicesAuthApi.set({
      id: svc.id,
      facility_id: facilityId,
      service_type: svc.service_type,
      status: serviceEdits.status,
      config,
    });
    setSavingService(null);
    if (result && result.error) {
      setServiceError(result.error);
    } else {
      setEditingService(null);
      fetchServices();
    }
  };

  const handleAddService = async () => {
    setSavingService("new");
    setServiceError(null);
    let config;
    try {
      config = JSON.parse(newService.config || "{}");
    } catch {
      setServiceError("Invalid JSON in config");
      setSavingService(null);
      return;
    }
    const result = await facilityServicesAuthApi.set({
      facility_id: facilityId,
      service_type: newService.service_type,
      status: newService.status,
      config,
    });
    setSavingService(null);
    if (result && result.error) {
      setServiceError(result.error);
    } else {
      setShowAddService(false);
      setNewService({
        service_type: "rag",
        status: "provisioning",
        config: "{}",
      });
      fetchServices();
    }
  };

  // ── USERS ─────────────────────────────────────────────────────────────
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteData, setInviteData] = useState({
    email: "",
    first_name: "",
    last_name: "",
    service_types: [],
    access_role: "user",
  });
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState(null);
  const [togglingUser, setTogglingUser] = useState(null);
  const [usersError, setUsersError] = useState(null);
  const [grantingFor, setGrantingFor] = useState(null); // user id
  const [grantForm, setGrantForm] = useState({
    service_type: "rag",
    access_role: "user",
  });
  const [grantSaving, setGrantSaving] = useState(false);
  const [showFacilityRoles, setShowFacilityRoles] = useState(null); // user.id
  const [facilityRolesForm, setFacilityRolesForm] = useState({ roles: [] });
  const [facilityRolesSaving, setFacilityRolesSaving] = useState(false);

  const handleGrantService = async (userId) => {
    setGrantSaving(true);
    const result = await authFetch("/facility-users/set-service-access", {
      method: "POST",
      body: JSON.stringify({
        user_id: userId,
        facility_id: facilityId,
        service_type: grantForm.service_type,
        access_role: grantForm.access_role,
      }),
    });
    setGrantSaving(false);
    if (!result?.error) {
      setGrantingFor(null);
      fetchUsers();
    }
  };

  const handleRevokeService = async (userId, serviceType) => {
    if (!window.confirm(`Revoke ${serviceType} access for this user?`)) return;
    await authFetch("/facility-users/set-service-access", {
      method: "POST",
      body: JSON.stringify({
        user_id: userId,
        facility_id: facilityId,
        service_type: serviceType,
        revoke: true,
      }),
    });
    fetchUsers();
  };

  const handleSetFacilityRoles = async (userId) => {
    setFacilityRolesSaving(true);
    await authFetch("/facility-users/set-facility-roles", {
      method: "POST",
      body: JSON.stringify({
        user_id: userId,
        facility_id: facilityId,
        facility_roles: facilityRolesForm.roles,
      }),
    });
    setFacilityRolesSaving(false);
    setShowFacilityRoles(null);
    setFacilityRolesForm({ roles: [] });
    fetchUsers();
  };

  const getFacilityRoleGroups = () => {
    const serviceTypes = services.map((s) => s.service_type);
    if (serviceTypes.length === 0) return FACILITY_ROLES_BY_PRODUCT;
    const visibleProducts = new Set(
      serviceTypes.flatMap((st) => SERVICE_TO_ROLE_GROUPS[st] || []),
    );
    const groups = FACILITY_ROLES_BY_PRODUCT.filter((g) =>
      visibleProducts.has(g.product),
    );
    return groups.length > 0 ? groups : FACILITY_ROLES_BY_PRODUCT;
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    const data = await authFetch(`/facility-users?facility_id=${facilityId}`);
    setUsers(data.users || []);
    setLoadingUsers(false);
  };

  useEffect(() => {
    fetchUsers();
  }, [facilityId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleUser = async (user) => {
    setTogglingUser(user.id);
    setUsersError(null);
    const result = await authFetch("/facility-users/toggle", {
      method: "POST",
      body: JSON.stringify({
        facility_id: facilityId,
        user_id: user.id,
        is_active: !user.is_active,
      }),
    });
    setTogglingUser(null);
    if (result && result.error) {
      setUsersError(result.error);
    } else {
      fetchUsers();
    }
  };

  // ── ADD EXISTING USER ────────────────────────────────────────────────
  const [showAddExisting, setShowAddExisting] = useState(false);
  const [allUsers, setAllUsers] = useState(null); // lazy-loaded once
  const [userSearch, setUserSearch] = useState("");
  const [existingSel, setExistingSel] = useState(null); // picked user
  const [existingForm, setExistingForm] = useState({
    service_types: [],
    access_role: "user",
  });
  const [addingExisting, setAddingExisting] = useState(false);
  const [addExistingError, setAddExistingError] = useState(null);

  const openAddExisting = async () => {
    setShowAddExisting(true);
    setShowInvite(false);
    setExistingSel(null);
    setUserSearch("");
    setAddExistingError(null);
    setExistingForm({
      service_types: services.map((s) => s.service_type),
      access_role: "user",
    });
    if (allUsers === null) {
      const result = await usersApi.getAll();
      setAllUsers(Array.isArray(result) ? result : result?.users || []);
    }
  };

  const existingMatches = () => {
    const q = userSearch.trim().toLowerCase();
    if (!q || !Array.isArray(allUsers)) return [];
    const memberEmails = new Set(users.map((u) => (u.email || "").toLowerCase()));
    return allUsers
      .filter((u) => {
        const hay =
          `${u.email || ""} ${u.first_name || ""} ${u.last_name || ""}`.toLowerCase();
        return hay.includes(q) && !memberEmails.has((u.email || "").toLowerCase());
      })
      .slice(0, 8);
  };

  const handleAddExisting = async () => {
    if (!existingSel) return;
    setAddingExisting(true);
    setAddExistingError(null);
    const result = await authFetch("/facility-users/invite", {
      method: "POST",
      body: JSON.stringify({
        facility_id: facilityId,
        email: existingSel.email,
        existing_only: true,
        service_types: existingForm.service_types,
        access_role: existingForm.access_role,
      }),
    });
    setAddingExisting(false);
    if (result && result.error) {
      setAddExistingError(result.error);
    } else {
      setShowAddExisting(false);
      fetchUsers();
    }
  };

  const handleInvite = async () => {
    setInviting(true);
    setInviteError(null);
    const result = await authFetch("/facility-users/invite", {
      method: "POST",
      body: JSON.stringify({
        ...inviteData,
        facility_id: facilityId,
        app_url: inviteDestination,
      }),
    });
    setInviting(false);
    if (result && result.error) {
      setInviteError(result.error);
    } else {
      setShowInvite(false);
      setInviteData({
        email: "",
        first_name: "",
        last_name: "",
        service_types: [],
        access_role: "user",
      });
      fetchUsers();
    }
  };

  // ── RENDER ────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={onBack}
          className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h3 className="text-lg font-semibold text-slate-900">
            {facilityName}
          </h3>
          <p className="text-sm text-slate-400 font-mono">{facilityId}</p>
        </div>
      </div>

      {/* ── DETAILS SECTION ── */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
        <h4 className="text-base font-semibold text-slate-800 mb-4">
          Facility Details
        </h4>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">
                Facility ID
              </label>
              <input
                type="text"
                value={formData.facility_id}
                disabled
                className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-slate-50 text-slate-500 font-mono text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">
                Facility Name *
              </label>
              <input
                type="text"
                value={formData.facility_name}
                onChange={(e) =>
                  setFormData({ ...formData, facility_name: e.target.value })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">
                Contact Email
              </label>
              <input
                type="email"
                value={formData.contact_email}
                onChange={(e) =>
                  setFormData({ ...formData, contact_email: e.target.value })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">
                Contact Phone
              </label>
              <input
                type="text"
                value={formData.contact_phone}
                onChange={(e) =>
                  setFormData({ ...formData, contact_phone: e.target.value })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">
              Address
            </label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) =>
                setFormData({ ...formData, address: e.target.value })
              }
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">
                Vertical
              </label>
              <select
                value={formData.vertical_id}
                onChange={(e) =>
                  setFormData({ ...formData, vertical_id: e.target.value })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              >
                {VERTICALS.map((v) => (
                  <option key={v.value} value={v.value}>
                    {v.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">
                Timezone
              </label>
              <select
                value={formData.timezone}
                onChange={(e) =>
                  setFormData({ ...formData, timezone: e.target.value })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              >
                {TIMEZONES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          {/* Chatbot / Branding */}
          <div className="border-t border-slate-100 pt-4">
            <h5 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
              Chatbot / Branding
            </h5>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Display Name
                </label>
                <input
                  type="text"
                  value={formData.display_name}
                  onChange={(e) =>
                    setFormData({ ...formData, display_name: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Agent Name
                </label>
                <input
                  type="text"
                  value={formData.agent_name}
                  onChange={(e) =>
                    setFormData({ ...formData, agent_name: e.target.value })
                  }
                  placeholder="e.g. Lidy, Ben"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">
                    State
                  </label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        state: e.target.value.toUpperCase().slice(0, 2),
                      })
                    }
                    placeholder="AZ"
                    maxLength={2}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">
                    Brand Color
                  </label>
                  <div className="flex items-center space-x-1">
                    <input
                      type="color"
                      value={formData.brand_color}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          brand_color: e.target.value,
                        })
                      }
                      className="w-9 h-9 rounded border border-slate-300 cursor-pointer p-0.5"
                    />
                    <input
                      type="text"
                      value={formData.brand_color}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          brand_color: e.target.value,
                        })
                      }
                      className="flex-1 px-2 py-2 border border-slate-300 rounded-lg text-xs font-mono focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Static Base URL
                </label>
                <input
                  type="text"
                  value={formData.static_base_url}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      static_base_url: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Allowed Origins
                </label>
                <input
                  type="text"
                  value={formData.allowed_origins}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      allowed_origins: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Source Registry Namespace
                </label>
                <input
                  type="text"
                  value={formData.source_registry_namespace}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      source_registry_namespace: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Sheets Log Tab
                </label>
                <input
                  type="text"
                  value={formData.sheets_log_tab}
                  onChange={(e) =>
                    setFormData({ ...formData, sheets_log_tab: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-xs font-medium text-slate-500 mb-1">
                System Prompt
              </label>
              <textarea
                value={formData.system_prompt}
                onChange={(e) =>
                  setFormData({ ...formData, system_prompt: e.target.value })
                }
                rows={5}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="mt-4">
              <label className="block text-xs font-medium text-slate-500 mb-1">
                Enrichment Rules (JSON)
              </label>
              <textarea
                value={formData.enrichment_rules}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    enrichment_rules: e.target.value,
                  })
                }
                rows={3}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* FieldIQ Tabs — only shown when the vertical defines tabs */}
          {fieldiqTabs.length > 0 && (
            <div className="border-t border-slate-100 pt-4">
              <div className="flex items-center justify-between mb-3">
                <h5 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  FieldIQ Tabs
                </h5>
                <span className="text-xs text-slate-400">
                  {formData.enabled_tabs == null
                    ? "Using vertical defaults"
                    : "Custom override"}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {fieldiqTabs.map((t) => (
                  <label
                    key={t.id}
                    className="flex items-center space-x-2 text-sm text-slate-700 px-3 py-2 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50"
                  >
                    <input
                      type="checkbox"
                      checked={effectiveTabIds.includes(t.id)}
                      onChange={() => toggleTab(t.id)}
                      className="rounded border-slate-300"
                    />
                    <span>{t.label}</span>
                  </label>
                ))}
              </div>

              {/* Features live in the same enabled_tabs array but are not tabs
                  — they switch something inside one. Separate heading so the
                  checkbox is not read as "show a Log Interaction tab". */}
              {fieldiqFeatures.length > 0 && (
                <div className="mt-4">
                  <h5 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                    FieldIQ Features
                  </h5>
                  <div className="grid grid-cols-3 gap-2">
                    {fieldiqFeatures.map((t) => (
                      <label
                        key={t.id}
                        className="flex items-center space-x-2 text-sm text-slate-700 px-3 py-2 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50"
                      >
                        <input
                          type="checkbox"
                          checked={effectiveTabIds.includes(t.id)}
                          onChange={() => toggleTab(t.id)}
                          className="rounded border-slate-300"
                        />
                        <span>
                          {t.label}
                          {t.parent && (
                            <span className="text-slate-400">
                              {" "}
                              · in {t.parent}
                            </span>
                          )}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {formData.enabled_tabs != null && (
                <button
                  onClick={() =>
                    setFormData({ ...formData, enabled_tabs: null })
                  }
                  className="mt-2 text-xs text-blue-600 hover:text-blue-700 font-medium"
                >
                  Reset to vertical defaults
                </button>
              )}
              <p className="mt-2 text-xs text-slate-400">
                Controls which tabs and in-tab features appear in the FieldIQ
                app for this facility. Applies on the next app load.
              </p>
            </div>
          )}

          {/* SLM Training */}
          <div className="border-t border-slate-100 pt-4">
            <h5 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
              SLM Training
            </h5>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Base Model
                </label>
                <select
                  value={formData.base_model_id}
                  onChange={(e) =>
                    setFormData({ ...formData, base_model_id: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                >
                  {BASE_MODELS.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Hardware Tier
                </label>
                <select
                  value={formData.model_tier}
                  onChange={(e) =>
                    setFormData({ ...formData, model_tier: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                >
                  {MODEL_TIERS.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-end pb-2">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.training_enabled}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        training_enabled: e.target.checked,
                      })
                    }
                    className="h-4 w-4 text-blue-600 rounded"
                  />
                  <span className="text-sm text-slate-600">
                    Training Enabled
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Webhook */}
          <div className="border-t border-slate-100 pt-4">
            <h5 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
              Webhook
            </h5>
            <div className="grid grid-cols-4 gap-4">
              <div className="col-span-3">
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Webhook URL
                </label>
                <input
                  type="url"
                  value={formData.webhook_url}
                  onChange={(e) =>
                    setFormData({ ...formData, webhook_url: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex items-end pb-2">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.webhook_enabled}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        webhook_enabled: e.target.checked,
                      })
                    }
                    className="h-4 w-4 text-blue-600 rounded"
                  />
                  <span className="text-sm text-slate-600">Enabled</span>
                </label>
              </div>
            </div>
          </div>

          {detailsError && (
            <p className="text-sm text-red-600">{detailsError}</p>
          )}
          <div className="flex justify-end pt-2">
            <button
              onClick={handleSaveDetails}
              disabled={savingDetails}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
            >
              <Save className="w-4 h-4" />
              <span>
                {savingDetails
                  ? "Saving..."
                  : detailsSaved
                    ? "Saved!"
                    : "Save Details"}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* ── OPENAI KEY ── */}
      <OpenAIKeyCard
        facilityId={facilityId}
        hint={facility.openai_api_key_hint}
      />

      {/* ── SERVICES SECTION ── */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-base font-semibold text-slate-800">
            Facility Services
          </h4>
          <button
            onClick={() => setShowAddService(true)}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center space-x-1"
          >
            <Plus className="w-4 h-4" />
            <span>Add Service</span>
          </button>
        </div>

        {serviceError && (
          <p className="text-sm text-red-600 mb-3">{serviceError}</p>
        )}

        {showAddService && (
          <div className="mb-4 p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-3">
            <h5 className="text-sm font-medium text-slate-700">New Service</h5>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-slate-500 mb-1">
                  Service Type
                </label>
                <select
                  value={newService.service_type}
                  onChange={(e) =>
                    setNewService({
                      ...newService,
                      service_type: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                >
                  {PRODUCT_CODES.filter((p) => p.type !== "combo").map((t) => (
                    <option key={t.value} value={t.type}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">
                  Status
                </label>
                <select
                  value={newService.status}
                  onChange={(e) =>
                    setNewService({ ...newService, status: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                >
                  {SERVICE_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">
                Config (JSON)
              </label>
              <textarea
                value={newService.config}
                onChange={(e) =>
                  setNewService({ ...newService, config: e.target.value })
                }
                rows={3}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono"
              />
            </div>
            <div className="flex space-x-2">
              <button
                onClick={handleAddService}
                disabled={savingService === "new"}
                className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {savingService === "new" ? "Adding..." : "Add"}
              </button>
              <button
                onClick={() => setShowAddService(false)}
                className="px-3 py-1.5 border border-slate-300 text-slate-600 rounded-lg text-sm hover:bg-slate-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {loadingServices ? (
          <p className="text-sm text-slate-400">Loading services...</p>
        ) : services.length === 0 ? (
          <p className="text-sm text-slate-400">No services configured.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">
                    Service
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">
                    Status
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">
                    Config
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">
                    Provisioned
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {services.map((svc) => (
                  <tr key={svc.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-800 uppercase">
                      {svc.service_type}
                    </td>
                    <td className="px-4 py-3">
                      {editingService === svc.id ? (
                        <select
                          value={serviceEdits.status}
                          onChange={(e) =>
                            setServiceEdits({
                              ...serviceEdits,
                              status: e.target.value,
                            })
                          }
                          className="px-2 py-1 border border-slate-300 rounded text-sm"
                        >
                          {SERVICE_STATUSES.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            svc.status === "active"
                              ? "bg-green-100 text-green-700"
                              : svc.status === "provisioning"
                                ? "bg-yellow-100 text-yellow-700"
                                : svc.status === "suspended"
                                  ? "bg-orange-100 text-orange-700"
                                  : "bg-slate-100 text-slate-600"
                          }`}
                        >
                          {svc.status}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      {editingService === svc.id ? (
                        <textarea
                          value={serviceEdits.config}
                          onChange={(e) =>
                            setServiceEdits({
                              ...serviceEdits,
                              config: e.target.value,
                            })
                          }
                          rows={4}
                          className="w-full px-2 py-1 border border-slate-300 rounded text-xs font-mono"
                        />
                      ) : (
                        <pre className="text-xs text-slate-500 truncate max-w-xs overflow-hidden">
                          {typeof svc.config === "object"
                            ? JSON.stringify(svc.config)
                            : svc.config || "{}"}
                        </pre>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-400">
                      {svc.provisioned_at
                        ? new Date(svc.provisioned_at).toLocaleDateString()
                        : "-"}
                    </td>
                    <td className="px-4 py-3">
                      {editingService === svc.id ? (
                        <div className="flex space-x-1">
                          <button
                            onClick={() => handleSaveService(svc)}
                            disabled={savingService === svc.id}
                            className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:opacity-50"
                          >
                            {savingService === svc.id ? "..." : "Save"}
                          </button>
                          <button
                            onClick={() => setEditingService(null)}
                            className="px-2 py-1 border border-slate-300 text-slate-600 rounded text-xs hover:bg-slate-50"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleEditService(svc)}
                          className="px-2 py-1 border border-slate-300 text-slate-600 rounded text-xs hover:bg-slate-50"
                        >
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── BUILDIQ CONFIG (construction service only) ── */}
      {services.some((s) => s.service_type === "construction") && (
        <BuildIQConfig facilityId={facilityId} />
      )}

      {/* ── USERS SECTION ── */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-base font-semibold text-slate-800">
            Facility Users
          </h4>
          <div className="flex items-center space-x-2">
            <button
              onClick={openAddExisting}
              className="px-3 py-1.5 border border-blue-600 text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-50 flex items-center space-x-1"
            >
              <UserPlus className="w-4 h-4" />
              <span>Add Existing</span>
            </button>
            <button
              onClick={() => {
                // Default: grant every service the facility has
                setInviteData((d) => ({
                  ...d,
                  service_types: services.map((s) => s.service_type),
                }));
                setShowAddExisting(false);
                setShowInvite(true);
              }}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center space-x-1"
            >
              <UserPlus className="w-4 h-4" />
              <span>Invite User</span>
            </button>
          </div>
        </div>

        {showAddExisting && (
          <div className="mb-4 p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-3">
            <h5 className="text-sm font-medium text-slate-700">
              Add an existing user to this facility
            </h5>
            {addExistingError && (
              <p className="text-sm text-red-600">{addExistingError}</p>
            )}
            <input
              type="text"
              autoFocus
              value={userSearch}
              onChange={(e) => {
                setUserSearch(e.target.value);
                setExistingSel(null);
              }}
              placeholder="Search by name or email…"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
            />
            {allUsers === null ? (
              <p className="text-xs text-slate-400">Loading users…</p>
            ) : (
              !existingSel &&
              userSearch.trim() && (
                <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg bg-white">
                  {existingMatches().length === 0 ? (
                    <p className="px-3 py-2 text-sm text-slate-400">
                      No matching users (existing members are hidden).
                    </p>
                  ) : (
                    existingMatches().map((u) => (
                      <button
                        key={u.id || u.email}
                        onClick={() => setExistingSel(u)}
                        className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm"
                      >
                        <span className="font-medium text-slate-800">
                          {[u.first_name, u.last_name].filter(Boolean).join(" ") ||
                            u.email}
                        </span>
                        <span className="text-slate-400 ml-2 text-xs">
                          {u.email}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              )
            )}
            {existingSel && (
              <div className="space-y-3">
                <p className="text-sm text-slate-600">
                  Adding{" "}
                  <span className="font-medium text-slate-800">
                    {existingSel.email}
                  </span>
                </p>
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2">
                    <label className="block text-xs text-slate-500 mb-1">
                      Service Access
                    </label>
                    <div className="flex flex-wrap gap-3 bg-white border border-slate-200 rounded-lg p-2.5">
                      {(services.length
                        ? services.map((s) => s.service_type)
                        : SERVICE_TYPES.map((t) => t.value)
                      ).map((svc) => (
                        <label
                          key={svc}
                          className="flex items-center space-x-1.5 cursor-pointer text-sm text-slate-700"
                        >
                          <input
                            type="checkbox"
                            checked={existingForm.service_types.includes(svc)}
                            onChange={() =>
                              setExistingForm((d) => ({
                                ...d,
                                service_types: d.service_types.includes(svc)
                                  ? d.service_types.filter((s) => s !== svc)
                                  : [...d.service_types, svc],
                              }))
                            }
                            className="h-3.5 w-3.5 text-blue-600 rounded border-slate-300"
                          />
                          <span>
                            {SERVICE_TYPES.find((t) => t.value === svc)?.label ||
                              svc}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">
                      Access Role
                    </label>
                    <select
                      value={existingForm.access_role}
                      onChange={(e) =>
                        setExistingForm({
                          ...existingForm,
                          access_role: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    >
                      {SERVICE_ACCESS_ROLES.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}
            <div className="flex space-x-2">
              <button
                onClick={handleAddExisting}
                disabled={
                  addingExisting ||
                  !existingSel ||
                  !existingForm.service_types.length
                }
                className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {addingExisting ? "Adding…" : "Add to Facility"}
              </button>
              <button
                onClick={() => setShowAddExisting(false)}
                className="px-3 py-1.5 border border-slate-300 text-slate-600 rounded-lg text-sm hover:bg-slate-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {usersError && (
          <p className="text-sm text-red-600 mb-3">{usersError}</p>
        )}

        {showInvite && (
          <div className="mb-4 p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-3">
            <h5 className="text-sm font-medium text-slate-700">Invite User</h5>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-3">
                <label className="block text-xs text-slate-500 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  value={inviteData.email}
                  onChange={(e) =>
                    setInviteData({ ...inviteData, email: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-slate-500 mb-1">
                  Which product is this for?
                </label>
                <select
                  value={inviteDestination}
                  onChange={(e) => setInviteDestination(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                >
                  {INVITE_DESTINATIONS.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-slate-400 mt-1">
                  Their set-password email and sign-in link point here.
                </p>
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">
                  First Name
                </label>
                <input
                  type="text"
                  value={inviteData.first_name}
                  onChange={(e) =>
                    setInviteData({ ...inviteData, first_name: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">
                  Last Name
                </label>
                <input
                  type="text"
                  value={inviteData.last_name}
                  onChange={(e) =>
                    setInviteData({ ...inviteData, last_name: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-slate-500 mb-1">
                  Service Access (all facility services pre-selected)
                </label>
                <div className="flex flex-wrap gap-3 bg-white border border-slate-200 rounded-lg p-2.5">
                  {(services.length
                    ? services.map((s) => s.service_type)
                    : SERVICE_TYPES.map((t) => t.value)
                  ).map((svc) => (
                    <label
                      key={svc}
                      className="flex items-center space-x-1.5 cursor-pointer text-sm text-slate-700"
                    >
                      <input
                        type="checkbox"
                        checked={inviteData.service_types.includes(svc)}
                        onChange={() =>
                          setInviteData((d) => ({
                            ...d,
                            service_types: d.service_types.includes(svc)
                              ? d.service_types.filter((s) => s !== svc)
                              : [...d.service_types, svc],
                          }))
                        }
                        className="h-3.5 w-3.5 text-blue-600 rounded border-slate-300"
                      />
                      <span>
                        {SERVICE_TYPES.find((t) => t.value === svc)?.label ||
                          svc}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">
                  Access Role
                </label>
                <select
                  value={inviteData.access_role}
                  onChange={(e) =>
                    setInviteData({
                      ...inviteData,
                      access_role: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                >
                  {SERVICE_ACCESS_ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {inviteError && (
              <p className="text-xs text-red-600">{inviteError}</p>
            )}
            <div className="flex space-x-2">
              <button
                onClick={handleInvite}
                disabled={inviting || !inviteData.email}
                className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {inviting ? "Inviting..." : "Send Invite"}
              </button>
              <button
                onClick={() => {
                  setShowInvite(false);
                  setInviteError(null);
                }}
                className="px-3 py-1.5 border border-slate-300 text-slate-600 rounded-lg text-sm hover:bg-slate-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {loadingUsers ? (
          <p className="text-sm text-slate-400">Loading users...</p>
        ) : users.length === 0 ? (
          <p className="text-sm text-slate-400">
            No users assigned to this facility.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">
                    User
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">
                    Role
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">
                    Services
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">
                    Facility Roles
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">
                    Status
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map((user) => (
                  <React.Fragment key={user.id}>
                    <tr className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-800">
                          {user.first_name} {user.last_name}
                        </div>
                        <div className="text-xs text-slate-400">
                          {user.email}
                        </div>
                      </td>
                      <td className="px-4 py-3 capitalize text-slate-600">
                        {user.role}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap items-center gap-1">
                          {(user.services || []).map((svc, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-mono"
                            >
                              {svc.service_type}:{svc.access_role}
                              <button
                                onClick={() =>
                                  handleRevokeService(user.id, svc.service_type)
                                }
                                className="ml-0.5 text-blue-400 hover:text-red-600"
                                title={`Revoke ${svc.service_type}`}
                              >
                                <X className="w-2.5 h-2.5" />
                              </button>
                            </span>
                          ))}
                          <button
                            onClick={() => {
                              setGrantingFor(
                                grantingFor === user.id ? null : user.id,
                              );
                              setGrantForm({
                                service_type: "rag",
                                access_role: "user",
                              });
                            }}
                            className="p-0.5 text-blue-600 hover:bg-blue-50 rounded"
                            title="Grant service access"
                          >
                            <Shield className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <FacilityRolesBadges
                          roles={user.facility_roles || []}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            user.is_active
                              ? "bg-green-100 text-green-700"
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          {user.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleToggleUser(user)}
                            disabled={togglingUser === user.id}
                            className={`px-2 py-1 rounded text-xs font-medium transition-colors disabled:opacity-50 ${
                              user.is_active
                                ? "border border-red-200 text-red-600 hover:bg-red-50"
                                : "border border-green-200 text-green-600 hover:bg-green-50"
                            }`}
                          >
                            {togglingUser === user.id
                              ? "..."
                              : user.is_active
                                ? "Disable"
                                : "Enable"}
                          </button>
                          <button
                            onClick={() => {
                              setFacilityRolesForm({
                                roles: user.facility_roles || [],
                              });
                              setShowFacilityRoles(user.id);
                            }}
                            className="p-1 text-purple-600 hover:bg-purple-50 rounded"
                            title="Manage facility roles"
                          >
                            <Tag className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {grantingFor === user.id && (
                      <tr className="bg-blue-50">
                        <td colSpan={6} className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <span className="text-xs font-medium text-slate-600">
                              Grant service:
                            </span>
                            <select
                              value={grantForm.service_type}
                              onChange={(e) =>
                                setGrantForm({
                                  ...grantForm,
                                  service_type: e.target.value,
                                })
                              }
                              className="px-2 py-1 border border-slate-300 rounded text-xs focus:ring-1 focus:ring-blue-500"
                            >
                              {SERVICE_TYPES.map((t) => (
                                <option key={t.value} value={t.value}>
                                  {t.label}
                                </option>
                              ))}
                            </select>
                            <select
                              value={grantForm.access_role}
                              onChange={(e) =>
                                setGrantForm({
                                  ...grantForm,
                                  access_role: e.target.value,
                                })
                              }
                              className="px-2 py-1 border border-slate-300 rounded text-xs focus:ring-1 focus:ring-blue-500"
                            >
                              <option value="viewer">Viewer</option>
                              <option value="user">User</option>
                              <option value="admin">Admin</option>
                            </select>
                            <button
                              onClick={() => handleGrantService(user.id)}
                              disabled={grantSaving}
                              className="flex items-center gap-1 px-2.5 py-1 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700 disabled:opacity-50"
                            >
                              <Check className="w-3 h-3" />
                              {grantSaving ? "Saving…" : "Grant"}
                            </button>
                            <button
                              onClick={() => setGrantingFor(null)}
                              className="px-2 py-1 border border-slate-300 text-slate-600 rounded text-xs hover:bg-slate-100"
                            >
                              Cancel
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── MANAGE FACILITY ROLES MODAL ── */}
      {showFacilityRoles &&
        (() => {
          const targetUser = users.find((u) => u.id === showFacilityRoles);
          if (!targetUser) return null;
          return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
                <h3 className="text-base font-semibold text-slate-800 mb-1">
                  Manage Roles — {targetUser.first_name} {targetUser.last_name}
                </h3>
                <p className="text-xs text-slate-500 mb-4">
                  Select the facility roles for this user at {facilityName}.
                </p>
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {getFacilityRoleGroups().map((group) => {
                    const GroupIcon = group.Icon;
                    return (
                      <div key={group.product}>
                        <div className="flex items-center gap-2 mb-2">
                          <GroupIcon className="w-4 h-4 text-slate-500" />
                          <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                            {group.label}
                          </span>
                        </div>
                        <div className="space-y-2 ml-6">
                          {group.roles.map((role) => (
                            <label
                              key={role.value}
                              className="flex items-start gap-3 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={facilityRolesForm.roles.includes(
                                  role.value,
                                )}
                                onChange={(e) => {
                                  setFacilityRolesForm((prev) => ({
                                    roles: e.target.checked
                                      ? [...prev.roles, role.value]
                                      : prev.roles.filter(
                                          (r) => r !== role.value,
                                        ),
                                  }));
                                }}
                                className="mt-0.5 h-4 w-4 text-blue-600 rounded"
                              />
                              <div>
                                <span
                                  className={`px-2 py-0.5 text-xs font-medium rounded-full ${role.color}`}
                                >
                                  {role.label}
                                </span>
                                <p className="text-xs text-slate-500 mt-0.5">
                                  {role.description}
                                </p>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-slate-100">
                  <button
                    onClick={() => {
                      setShowFacilityRoles(null);
                      setFacilityRolesForm({ roles: [] });
                    }}
                    className="px-4 py-2 border border-slate-300 text-slate-600 rounded-lg text-sm hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleSetFacilityRoles(showFacilityRoles)}
                    disabled={facilityRolesSaving}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50"
                  >
                    {facilityRolesSaving ? "Saving..." : "Save Roles"}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
    </div>
  );
};

export default FacilityDetail;
